import React from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import FormHeader from 'components/shared/form/FormHeader';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import { ATTR_OLIO_MAPPED_FIELDS } from 'constants/attrMappedFields';
import { useProfile } from 'context/profile';
import { useAdminForm } from 'hooks/datasource';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminAttrsQueryKeys, adminShowAttr, createAttr, updateAttr } from 'services/api/admin/attrs';
import { adminIndexClients } from 'services/api/admin/clients';

type OlioFieldOption = { label: string; value: string; apiName: string };

const schema = yup.object({
  name: yup.string().required('Raw attribute is required'),
  client: yup.mixed().test('client', 'Client is required', (v: any) => !!v?.id),
  displayName: yup.string().required('Display attribute is required'),
});

function isValidJSON(str: string): boolean {
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
}

const baseAttrMatchRules = { '*': ['%{value}'] };

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;

export default function EditAttribute() {
  const { id } = useParams();
  const isEditing = !!id;
  const { profile } = useProfile();

  const canEdit = profile.has(PERMISSIONS.adminAttrEdit);

  const form = useAdminForm({
    id,
    show: adminShowAttr,
    create: createAttr,
    update: (updateId, payload) => updateAttr({ id: updateId, ...payload }),
    include: ['client'] as const,
    queryKey: adminAttrsQueryKeys,
    schema,
    toFormValues: (data) => {
      const olioAttrOption = data ? ATTR_OLIO_MAPPED_FIELDS.find((f) => f.value === data.olioAttr) : undefined;
      const matchRulesStr =
        data?.matchRules && Object.keys(data.matchRules).length
          ? JSON.stringify(data.matchRules, undefined, 2)
          : JSON.stringify(baseAttrMatchRules, undefined, 2);

      return {
        name: data?.name ?? '',
        displayName: data?.displayName ?? '',
        client: data?.client ?? null,
        olioAttr: olioAttrOption ?? null,
        visible: data?.visible ?? false,
        active: data?.active ?? true,
        matchRules: matchRulesStr,
      };
    },
    toPayload: (v) => {
      const hasOlioAttr = !!v.olioAttr?.value;
      const cleanedJSON = v.matchRules?.replace(/\n/g, '');

      return {
        name: v.name ?? '',
        displayName: v.displayName || null,
        olioAttr: v.olioAttr?.value || null,
        clientId: v.client?.id || '',
        visible: v.visible ?? false,
        active: v.active ?? true,
        matchRules: hasOlioAttr && cleanedJSON && isValidJSON(cleanedJSON) ? JSON.parse(cleanedJSON) : {},
      };
    },
    onCreateSuccess: { toast: 'Attribute successfully added!', navigate: '/admin/attributes' },
    onUpdateSuccess: { toast: 'Attribute successfully updated!', navigate: '/admin/attributes' },
  });

  const clientOpts = useInfiniteOptions({
    queryKey: ['adminClients', 'attributes'],
    fetchFn: adminIndexClients,
    params: { sortBy: 'name asc' },
  });

  const handleOlioAttrChange = (newValue: OlioFieldOption | null) => {
    if (newValue) {
      const displayName = ATTR_OLIO_MAPPED_FIELDS.find((f) => f.value === newValue.value)?.label;
      form.setValue('displayName', displayName || form.values.name || '');
    } else {
      form.setValue('displayName', form.values.name || '');
    }
    form.setValue('olioAttr', newValue);
    form.setValue('visible', false);
  };

  if (isEditing && form.loading) return <CenteredSpinner />;

  return (
    <AdminFormPage>
      <FormHeader title={`${isEditing ? 'Edit' : 'Add'} Attribute`} />

      <InputGroup title='Client' error={form.errors.client}>
        <Select
          {...clientOpts}
          value={form.values.client}
          onChange={(v) => form.setValue('client', v)}
          getOptionValue={getId}
          getOptionLabel={getName}
          placeholder='Select...'
          isDisabled={isEditing}
        />
      </InputGroup>

      <SectionHeader>Attribute Details</SectionHeader>

      <InputGroup title='Raw Attribute' error={form.errors.name}>
        <Input
          value={form.values.name ?? ''}
          onChange={(e) => form.setValue('name', e.target.value)}
          placeholder='Type...'
          $hasError={!!form.errors.name}
          disabled={isEditing}
          onBlur={() => {
            if (form.values.name && !form.values.displayName) form.setValue('displayName', form.values.name);
          }}
        />
      </InputGroup>

      <InputGroup title='Mapped Olio Field'>
        <Select
          options={ATTR_OLIO_MAPPED_FIELDS}
          value={form.values.olioAttr}
          onChange={handleOlioAttrChange}
          getOptionValue={(o) => o!.value}
          getOptionLabel={(o) => o!.label}
          placeholder='Type...'
          isClearable
          isDisabled={!canEdit}
        />
      </InputGroup>

      {!form.values.olioAttr && (
        <CheckboxRow>
          <input
            type='checkbox'
            checked={form.values.visible ?? false}
            onChange={(e) => form.setValue('visible', e.target.checked)}
            disabled={!canEdit}
          />
          <span>Display on UI</span>
        </CheckboxRow>
      )}

      <InputGroup title='Display Attribute' error={form.errors.displayName}>
        <Input
          value={form.values.displayName ?? ''}
          onChange={(e) => form.setValue('displayName', e.target.value)}
          placeholder='Type...'
          $hasError={!!form.errors.displayName}
          disabled={!!form.values.olioAttr || !canEdit}
        />
      </InputGroup>

      {!!form.values.olioAttr && (
        <>
          <SectionHeader>Import Matching Logic</SectionHeader>
          <InputGroup title='Please provide any matching logic for the attribute (JSON FORMAT)'>
            <StyledTextarea
              value={form.values.matchRules ?? ''}
              onChange={(e) => form.setValue('matchRules', e.target.value)}
            />
          </InputGroup>
        </>
      )}

      <ButtonGroup style={{ justifyContent: 'flex-end' }}>
        <Button variant='ghost' onClick={form.goBack}>
          Cancel
        </Button>
        <Button onClick={form.submit} disabled={!form.valid || form.submitting} loading={form.submitting}>
          Submit
        </Button>
      </ButtonGroup>
    </AdminFormPage>
  );
}

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
  }
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  height: 200px;
  border: 1px solid var(--black-25);
  border-radius: 4px;
  padding: 8px;
  font-size: 13px;
  line-height: 18px;
  font-family: monospace;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const CenteredSpinner = styled(CircularProgress)`
  margin: 64px auto;
  display: block;
`;
